import { Logo } from "@/components/logo";
import {
  parseOkamiMessage,
  READY_STATUS,
  TokenStatus,
  TokenStatusType,
} from "@/utils/extension-bridge";
import { createFileRoute } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export const Route = createFileRoute("/_app/extension/auth")({
  component: AuthExtensionPage,
});

type PageStatus = TokenStatusType | "IDLE";

function AuthExtensionPage() {
  const navigate = Route.useNavigate();

  const [tokenStatus, setTokenStatus] = useState<PageStatus>("IDLE");
  const [notDetected, setNotDetected] = useState(false);
  const [statusDetails, setStatusDetails] = useState<{
    message: string;
    error: string | null;
  }>({
    message: "Aguardando extensão...",
    error: null,
  });

  const redirectTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // only trust same-window, same-origin, schema-valid messages
      if (event.source !== window) return;
      if (event.origin !== window.location.origin) return;

      const message = parseOkamiMessage(event.data);
      if (!message) return;

      setNotDetected(false);
      setTokenStatus(message.status);
      setStatusDetails({
        message: message.message || "Processando...",
        error: message.error || null,
      });

      if (message.status === TokenStatus.SAVED) {
        redirectTimer.current = setTimeout(() => {
          navigate({
            to: "/works",
            search: { category: null, name: null, status: null, tagId: null },
          });
        }, 2000);
      }
    }

    window.addEventListener("message", handleMessage);

    // ask the content script to replay its last status (handshake / race fix)
    window.postMessage(
      { okami: true, status: READY_STATUS },
      window.location.origin,
    );

    // no message after a while -> extension likely not installed/active
    const detectTimer = setTimeout(() => setNotDetected(true), 8000);

    return () => {
      window.removeEventListener("message", handleMessage);
      clearTimeout(detectTimer);
      if (redirectTimer.current) clearTimeout(redirectTimer.current);
    };
  }, [navigate]);

  const getStatusIcon = () => {
    if (notDetected) return <AlertCircle className="size-10 text-gray-400" />;
    switch (tokenStatus) {
      case TokenStatus.FETCHING:
        return <Loader2 className="animate-spin size-10 text-blue-500" />;
      case TokenStatus.SUCCESS:
      case TokenStatus.SAVED:
        return <CheckCircle2 className="size-10 text-green-500" />;
      case TokenStatus.ERROR:
        return <XCircle className="size-10 text-red-500" />;
      default:
        return <AlertCircle className="size-10 text-gray-400" />;
    }
  };

  const getStatusMessage = () => {
    if (notDetected) return "Extensão não detectada";
    switch (tokenStatus) {
      case TokenStatus.FETCHING:
        return "Conectando com sua conta...";
      case TokenStatus.SUCCESS:
        return "Autenticação bem-sucedida!";
      case TokenStatus.SAVED:
        return "Extensão configurada com sucesso!";
      case TokenStatus.ERROR:
        return "Falha na autenticação";
      default:
        return "Aguardando extensão...";
    }
  };

  const getStatusDescription = () => {
    if (notDetected)
      return "Verifique se a extensão Okami está instalada e ativa, depois recarregue a página.";
    switch (tokenStatus) {
      case TokenStatus.FETCHING:
        return "Buscando suas credenciais de acesso...";
      case TokenStatus.SUCCESS:
        return "Salvando suas credenciais na extensão...";
      case TokenStatus.SAVED:
        return "Redirecionando em instantes...";
      case TokenStatus.ERROR:
        return statusDetails.error
          ? `Erro: ${statusDetails.message}`
          : "Verifique se você está logado na plataforma";
      default:
        return "Certifique-se de que a extensão Okami está instalada";
    }
  };

  return (
    <div className="flex min-h-screen grid-cols-2 flex-col items-center justify-center antialiased md:grid">
      <div className="hidden h-full flex-col justify-between border-r border-foreground/5 bg-muted p-10 text-muted-foreground md:flex">
        <main className=" flex h-full flex-col items-center justify-center gap-5  text-lg  text-foreground">
          <Logo className="h-[400px] w-[400px]" />
          <span className="mt font-semibold ">Okami Platform</span>
        </main>

        <footer className="text-sm">
          Okami Web - Dashboard &copy; {new Date().getFullYear()}
        </footer>
      </div>

      <div className="flex flex-col items-center justify-center px-6">
        <div className="flex md:hidden mb-8">
          <Logo className="h-[100px] w-[100px]" />
        </div>

        <div className="flex justify-center items-center flex-col gap-6 max-w-md text-center">
          {getStatusIcon()}

          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-foreground">
              {getStatusMessage()}
            </h2>
            <p className="text-muted-foreground">{getStatusDescription()}</p>
          </div>

          {tokenStatus === TokenStatus.ERROR && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Dica:</strong> Faça login na plataforma Okami em outra
                aba e tente novamente.
              </p>
            </div>
          )}

          {tokenStatus === "IDLE" && !notDetected && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                A extensão está verificando sua sessão automaticamente...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
